import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Loader from "../../../../../components/Loader";
import { showSnackbar } from "../../../../../slice/snackbarSlice";
import {
  getDueDateHistory,
  updateTaskDueDate,
} from "../../../../../thunks/instruction";
import {
  NOTIFICATION_MESSAGE,
  VALIDATION_MSG,
} from "../../../../../utils/constant";
import { getAllV2Tasks } from "../../../../../thunks/task";
import { getTaskSubTask } from "../../../../../thunks/dashboard";

// Small component for rendering history item
const HistoryCard = React.memo(({ data, formatHistory }) => {
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "16px",
          marginBottom: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #F0F0F0",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            borderColor: "#00CE3F",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "#E8F5E8",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "16px",
            flexShrink: 0,
          }}
        >
          <CalendarMonthIcon sx={{ color: "#00CE3F", fontSize: "20px" }} />
        </Box>
        <Typography
          sx={{
            flex: 1,
            lineHeight: 1.6,
            color: "#333",
            fontSize: "14px",
          }}
        >
          {formatHistory(data)}
        </Typography>
      </Box>
    </Grid>
  );
});

const V2DueDateDialog = ({
  open,
  handleClose = () => {},
  selectedData = {},
  isV2Dashboard = true,
  getAllTasks,
  subTasksMap = {},
  subTask: originalSubTask = [],
}) => {
  // Create a proper state/variable for subTasks
  const subTasks = useMemo(() => {
    return subTasksMap[selectedData?.taskId] || originalSubTask || [];
  }, [subTasksMap, selectedData?.id, originalSubTask]);

  const dispatch = useDispatch();
  const params = useParams();
  const [isLoader, setLoader] = useState(false);
  const [dueDateHistory, setDueDateHistory] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dateWarn, setDateWarn] = useState(false);
  const [updateDueDateData, setOpenDueDateData] = useState({
    dueDate: new Date(selectedData?.dueDate),
    note: "",
  });
  const [isError, setIsError] = useState(false);

  // Filter state
  const [historyFilter, setHistoryFilter] = useState("All");

  // Pagination - Fixed state management
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const historyContainerRef = useRef(null);

  const handleUpdateValue = (value, key) => {
    setIsError(false);
    setOpenDueDateData((prev) => ({ ...prev, [key]: value }));
    if (key === "dueDate") {
      const selectedDate = new Date(value);
      setDateWarn(selectedDate < new Date());
    }
  };

  const handleUpdateDueDate = async () => {
    if (!updateDueDateData?.note) {
      setIsError(true);
      return;
    }
    try {
      const payload = {
        id: selectedData?.id,
        dueDate: updateDueDateData?.dueDate,
        note: updateDueDateData?.note,
      };
      setLoader(true);
      const response = await dispatch(updateTaskDueDate(payload)).unwrap();

      // Pass updated data back to parent for immediate UI update
      handleClose({
        id: selectedData?.id,
        dueDate: updateDueDateData?.dueDate,
      });

      // Refresh tasks in background
      if (getAllTasks) {
        getAllTasks();
      }
      if (response) {
        setLoader(false);
        dispatch(
          showSnackbar({
            show: true,
            message: NOTIFICATION_MESSAGE?.DUE_DATE_UPDATE_SUCCESS,
            severity: "success",
          })
        );
      }
    } catch (error) {
      setLoader(false);
      dispatch(
        showSnackbar({
          show: true,
          message: error?.message,
          severity: "error",
        })
      );
      handleClose();
    }
  };

  const handleChangeTabValue = (event, newVal) => {
    setTabValue(newVal);
    if (newVal === 0) {
      // Reset pagination when switching to history tab
      setPage(1);
      setDueDateHistory([]);
      setHasMore(true);
    }
  };

  const formatDate = (date) =>
    date ? format(new Date(date), "dd/MM/yyyy hh:mm a") : "NA";

  const formatHistory = (item) => {
    if (item.sourceTable === "InstructionTaskDueDatePusherHistory") {
      return (
        <>
          Task <b>{item.taskName}</b> was due by{" "}
          <b>{formatDate(item.pastDueDate)}</b> pushed to{" "}
          <b>{formatDate(item.newDueDate)}</b> by <b>{item.userName || "NA"}</b>{" "}
          on <b>{formatDate(item.createdAt)}</b> for reason:{" "}
          <b>{item.notes || "NA"}</b>
        </>
      );
    } else if (item.sourceTable === "InstructionTaskHistory") {
      return (
        <>
          Task <b>{item.taskName}</b> status changed to{" "}
          <b>{item.status || "NA"}</b> by <b>{item.userName || "NA"}</b> on{" "}
          <b>{formatDate(item.createdAt)}</b>
        </>
      );
    } else {
      return (
        <>
          Task <b>{item.taskName}</b> updated by <b>{item.userName || "NA"}</b>{" "}
          on <b>{formatDate(item.createdAt)}</b>
        </>
      );
    }
  };

  const getDueDateHistoryData = async (currentPage = 1, reset = false) => {
    try {
      if (currentPage === 1) {
        setLoader(true);
      } else {
        setIsLoadingMore(true);
      }

      let taskIds = [];
      if (historyFilter === "All") {
        taskIds = [selectedData?.id, ...(subTasks?.map((st) => st.id) || [])];
      } else if (historyFilter === "Main Task") {
        taskIds = [selectedData?.id];
      } else {
        taskIds = [historyFilter]; // <-- subtask id
      }

      const payload = { page: currentPage, limit: 10, taskIds };
      const response = await dispatch(getDueDateHistory(payload)).unwrap();

      const newData = response.data || response || [];

      if (reset || currentPage === 1) {
        setDueDateHistory(newData);
      } else {
        setDueDateHistory((prev) => [...prev, ...newData]);
      }

      // Determine if there are more pages
      const hasMoreData = newData.length === payload.limit;
      setHasMore(hasMoreData);
    } catch (error) {
      console.error("Error fetching history:", error);
      if (currentPage === 1) {
        setDueDateHistory([]);
      }
      setHasMore(false);
    } finally {
      setLoader(false);
      setIsLoadingMore(false);
    }
  };

  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setHistoryFilter(newFilter);
    setPage(1);
    setDueDateHistory([]);
    setHasMore(true);
  };

  // Fixed scroll handler
  const handleScroll = useCallback(
    (e) => {
      if (isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;

      // Trigger when user scrolls to 80% of the container height
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        setIsLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);
        getDueDateHistoryData(nextPage, false);
      }
    },
    [page, isLoadingMore, hasMore]
  );

  // Initial data fetch when dialog opens or filters change
  useEffect(() => {
    if (open && tabValue === 0) {
      getDueDateHistoryData(1, true);
    }
  }, [open, tabValue, historyFilter]);

  // Reset states when dialog opens
  useEffect(() => {
    if (open) {
      setPage(1);
      setDueDateHistory([]);
      setHasMore(true);
      setIsLoadingMore(false);
      setTabValue(0);
      setHistoryFilter("All");
    }
  }, [open]);

  const renderedHistory = useMemo(
    () =>
      dueDateHistory.map((data, index) => (
        <HistoryCard
          key={`${data.id || index}-${index}`}
          data={data}
          formatHistory={formatHistory}
        />
      )),
    [dueDateHistory]
  );

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "28px",
          paddingBottom: 0,
          maxHeight: "500px",
          width: "90%",
          maxWidth: "800px",
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => handleClose()}
        sx={{ position: "absolute", right: 8, top: 8, zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        dividers
        sx={{ marginRight: "5px", padding: "32px", backgroundColor: "#FCFCFC" }}
      >
        {isLoader && <Loader />}

        {/* Fixed Header with Tabs */}
        <Grid
          item
          xs={12}
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#FFFFFF",
            paddingTop: "0px",
            paddingBottom: "16px",
            width: "100%",
            marginTop: "-32px",
            zIndex: 1,
          }}
        >
          <Tabs
            fullWidth
            value={tabValue}
            onChange={handleChangeTabValue}
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: "#00CE3F" },
              "& .MuiTab-root.Mui-selected": {
                color: "#00CE3F",
                fontWeight: 600,
              },
              "& .MuiTab-root": { color: "#3D4740" },
            }}
          >
            <Tab label="View History" />
            <Tab label="Update Due Date" />
          </Tabs>
        </Grid>

        {/* Filter - Only show when on History tab AND subtasks are available */}
        {/* {tabValue === 0 && subTasks && subTasks.length > 0 && ( */}
        {tabValue === 0 &&
          !selectedData?.isSubTask &&
          subTasks &&
          subTasks.length > 0 && (
            <Grid
              item
              xs={12}
              sx={{
                mt: 2,
                mb: 2,
                position: "sticky",
                top: "80px",
                backgroundColor: "#FCFCFC",
                zIndex: 1,
                paddingBottom: "8px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <FormControl sx={{ minWidth: 250 }}>
                  <Select
                    value={historyFilter}
                    className="textFieldHistory_outlined_border"
                    onChange={handleFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: "#F8F9FA",
                    }}
                  >
                    <MenuItem value="All">All Tasks</MenuItem>
                    <MenuItem value="Main Task">
                      {selectedData?.name || "Main Task"}
                    </MenuItem>
                    {subTasks.map((task) => {
                      return (
                        <MenuItem key={task.id} value={task.id}>
                          {task.taskName || task.name || `Subtask ${task.id}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          )}

        {/* View History */}
        {tabValue === 0 && (
          <>
            {/* History List */}
            <Box
              ref={historyContainerRef}
              onScroll={handleScroll}
              sx={{
                maxHeight: "350px",
                overflowY: "auto",
                overflowX: "hidden",
                backgroundColor: "#FAFAFA",
                borderRadius: "16px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <Grid container spacing={2}>
                {dueDateHistory.length > 0
                  ? renderedHistory
                  : !isLoader && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 6,
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            border: "2px dashed #E0E0E0",
                          }}
                        >
                          <CalendarMonthIcon
                            sx={{ fontSize: 48, color: "#CCC", mb: 2 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "16px", fontWeight: 500 }}
                          >
                            No history available
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                {isLoadingMore && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress
                        size={28}
                        sx={{ color: "#00CE3F", mr: 2 }}
                      />
                      <Typography sx={{ fontWeight: 500 }}>
                        Loading more history...
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {!hasMore && dueDateHistory.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        marginTop: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: "italic",
                          fontSize: "13px",
                          fontWeight: 500,
                        }}
                      >
                        All history loaded
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </>
        )}

        {/* Update Due Date */}
        {tabValue === 1 && (
          <Box sx={{ mt: 4 }}>
            <Grid item xs={12} sx={{ mb: 3 }}>
              <TextField
                type="text"
                label="Add Note"
                fullWidth
                placeholder="Enter a Note"
                multiline
                rows={5}
                value={updateDueDateData?.note}
                onChange={(e) => handleUpdateValue(e?.target?.value, "note")}
                error={isError && !updateDueDateData?.note}
                helperText={
                  isError && !updateDueDateData?.note && VALIDATION_MSG.REQUIRED
                }
                sx={{
                  "& label.Mui-focused": {
                    color: "#00ce3f",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#00ce3f",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00ce3f",
                    },
                  },
                }}
              />
            </Grid>

            {/* Date Time Picker --> Due date */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    PopperProps={{
                      anchorOrigin: { vertical: "top", horizontal: "center" },
                      transformOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                      },
                      getContentAnchorEl: null,
                    }}
                    slotProps={{
                      textField: {
                        InputLabelProps: { shrink: true },
                        readOnly: true,
                      },
                    }}
                    label="Due Date"
                    //minDate={new Date()} //TO DISABLE PAST DATES
                    format="dd/MM/yyyy hh:mm a"
                    className="normal_normal_600_12_Manropee outlined_border"
                    value={updateDueDateData?.dueDate}
                    onChange={(e) => handleUpdateValue(e, "dueDate")}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </FormControl>
              {dateWarn && (
                <Typography
                  component="div"
                  sx={{
                    color: "orange",
                    fontSize: "12px",
                    mt: 1,
                    ml: 2,
                  }}
                >
                  Due date selected is before the current date and time.
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem 0 0 0",
                gap: "1rem",
              }}
            >
              <Button
                className="cancel_button"
                style={{ width: "130px" }}
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                className="save_button"
                onClick={() => handleUpdateDueDate()}
              >
                Save
              </Button>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default V2DueDateDialog;
